'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';


interface HeroSectionProps {
  onQuoteClick: () => void;
}

const HeroSection = ({ onQuoteClick }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroSlides = [
  {
    image: "/assets/images/patio-pressure-washer-phoenix-az-1765467747968.jpg",
    alt: 'Professional worker in blue coveralls, yellow gloves and yellow boots using pressure washing equipment on modern concrete patio with decorative rock landscaping, metal fencing, and residential setting in Phoenix',
    title: 'Transform Your Desert Oasis',
    subtitle: 'Expert Patio Cleaning for Phoenix Metro Homes'
  },
  {
    image: "/assets/images/power-washing-patio-phx-az-1765466153176.jpg",
    alt: 'Professional worker in blue overalls and green cap using pressure washer to clean covered patio with brick paver flooring under pergola structure in Phoenix residential backyard',
    title: 'Restore Your Outdoor Living Space',
    subtitle: 'Professional Results That Last Through Desert Conditions'
  },
  {
    image: "/assets/images/residential-paver-cleaning-phoenix-1765466322222.jpg",
    alt: 'Aerial overhead view of professional residential paver cleaning service in Phoenix showing worker in high-visibility yellow safety jacket using pressure washing equipment on concrete pavers with various surface textures and patterns visible',
    title: 'Desert-Tough Cleaning Solutions',
    subtitle: 'Specialized Care for Phoenix Climate Challenges'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, heroSlides.length]);

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
  };

  if (!isHydrated) {
    return (
      <div className="relative h-[600px] md:h-[700px] bg-muted">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="relative h-[600px] md:h-[700px] overflow-hidden">
      {/* Background Slides */}
      {heroSlides.map((slide, index) =>
      <div
        key={index}
        className={`absolute inset-0 transition-opacity duration-1000 ${
        index === currentSlide ? 'opacity-100' : 'opacity-0'}`
        }>

          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70 z-10"></div>
          <AppImage
          src={slide.image}
          alt={slide.alt}
          className="w-full h-full object-cover" />

        </div>
      )}

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="font-headline font-bold text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
              {heroSlides[currentSlide].title}
            </h1>
            <p className="font-body text-xl md:text-2xl mb-8 opacity-95">
              {heroSlides[currentSlide].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="px-8 py-4 bg-accent text-accent-foreground rounded-lg font-body font-semibold text-lg hover:bg-accent/90 transition-brand shadow-cta-glow hover:shadow-cta-glow-hover">

                Get Free Quote
              </button>
              <Link
                href="/services"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-lg font-body font-semibold text-lg hover:bg-white/20 transition-brand">

                View Services
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-0 right-0 z-30 flex justify-center gap-3">
        {heroSlides.map((_, index) =>
        <button
          key={index}
          onClick={() => handleSlideChange(index)}
          className={`w-3 h-3 rounded-full transition-brand ${
          index === currentSlide ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>
    </div>);

};

export default HeroSection;